import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Briefcase, ChevronDown, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { useOrganization, useWorkspaces, useUserOrgs, useSwitchOrg } from "../../hooks/useAPI";
import { ACTIVE_WS_KEY } from "./WorkspaceDashboardPage";
import { CreateOrgModal } from "./CreateOrgModal";

// Defense-in-depth: string-redact errors so closure refs back to request
// payloads can't leak into devtools. Matches the helper used across the
// other converged pages.
function redactErrorForDev(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  return String(err);
}

// `getUserOrgs()` returns `any[]` from api.ts (backend-types gap — should
// be fixed in api.ts). This local row interface gives the dropdown the
// minimum field set it needs without leaking `any` into the component.
interface UserOrgRow {
  id?: string;
  _id?: string;
  name: string;
}
interface WorkspaceRow {
  id?: string;
  _id?: string;
  name: string;
}

function getRowId(row: { id?: string; _id?: string }): string | undefined {
  return row._id ?? row.id;
}

interface WorkspaceSwitcherProps {
  compact?: boolean;
  isDemoMode?: boolean;
  inline?: boolean;
  large?: boolean;
}

export function WorkspaceSwitcher({
  compact = true,
  isDemoMode = false,
  inline = false,
  large = false,
}: WorkspaceSwitcherProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const orgTriggerRef = useRef<HTMLButtonElement>(null);
  const orgToggleId = useId();
  const orgListId = useId();

  const { data: activeOrg } = useOrganization();
  const { data: workspaces } = useWorkspaces() as { data: WorkspaceRow[] | undefined };
  const { data: userOrgsData = [] } = useUserOrgs();
  const userOrgs: UserOrgRow[] = userOrgsData as UserOrgRow[];
  const switchOrgMutation = useSwitchOrg();

  // Seed active-workspace from localStorage; fall back to the first one
  // the backend returns. Only re-seeds while activeWorkspaceId is null
  // — never overwrites a deliberate choice the user just made.
  useEffect(() => {
    if (workspaces && workspaces.length > 0 && !activeWorkspaceId) {
      // localStorage access can throw SecurityError in some embedded
      // contexts; fall back to "no stored value" so the user still
      // lands on the first workspace.
      let stored: string | null = null;
      try { stored = localStorage.getItem(ACTIVE_WS_KEY); } catch { stored = null; }
      const valid = stored && workspaces.some((w) => getRowId(w) === stored);
      const firstId = getRowId(workspaces[0]);
      setActiveWorkspaceId(valid ? stored : (firstId ?? null));
    }
  }, [workspaces, activeWorkspaceId]);

  // ESC + click-outside dismissal for the org dropdown. ESC was missing
  // entirely pre-fix — keyboard users had no way to dismiss without
  // tabbing away. Mousedown rather than click so a drag-from-inside
  // doesn't close on mouse-up over the backdrop.
  useEffect(() => {
    if (!isOrgDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
        setIsOrgDropdownOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOrgDropdownOpen(false);
        // WAI-ARIA menubutton pattern: focus must return to the invoking
        // element on dismissal. Pre-fix ESC dropped focus to <body>.
        orgTriggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOrgDropdownOpen]);

  const activeWorkspace =
    workspaces?.find((w) => getRowId(w) === activeWorkspaceId) ?? workspaces?.[0];

  if (!activeOrg || !activeWorkspace) return null;

  const goToWorkspaces = () => {
    if (!isDemoMode) navigate("/dashboard/workspaces");
  };

  const handleSwitchOrg = (orgId: string) => {
    const activeId = getRowId(activeOrg as { id?: string; _id?: string });
    if (orgId === activeId || switchOrgMutation.isPending) return;
    switchOrgMutation.mutate(orgId, {
      onSuccess: () => {
        setIsOrgDropdownOpen(false);
        // SPA navigation + cache reset rather than `window.location.href`
        // — the prior full reload discarded the React-Query cache and
        // forced every dashboard query to re-fetch from a cold start.
        // Invalidate-all is the right knob: queries scoped to the old
        // org must NOT serve stale data after the switch.
        queryClient.invalidateQueries();
        navigate("/dashboard");
      },
      onError: (err) => {
        if (import.meta.env.DEV) {
          console.warn("[WorkspaceSwitcher] switchOrg failed:", redactErrorForDev(err));
        }
        toast.error("Failed to switch organization. Please try again.");
      },
    });
  };

  const orgDropdown = (minWidth?: string) => (
    <div
      id={orgListId}
      role="menu"
      aria-labelledby={orgToggleId}
      className="adp-dropdown"
      style={{ minWidth: minWidth ?? "200px" }}
    >
      <div className="px-2 pt-2 pb-1">
        <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground">
          Organizations
        </span>
      </div>
      <div className="border-t border-border" aria-hidden="true" />
      {userOrgs.map((org) => {
        const orgId = getRowId(org);
        const activeId = getRowId(activeOrg as { id?: string; _id?: string });
        const isActive = orgId === activeId;
        const isPending = switchOrgMutation.isPending;
        return (
          <button
            key={orgId ?? org.name}
            type="button"
            role="menuitem"
            onClick={() => orgId && handleSwitchOrg(orgId)}
            disabled={isActive || isPending || !orgId}
            aria-current={isActive ? "true" : undefined}
            aria-busy={isPending && !isActive ? true : undefined}
            className={cn(
              "w-full flex items-center gap-2 px-2 py-2 text-left text-sm transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0 disabled:cursor-not-allowed",
              isActive ? "bg-[var(--brand-surface)] text-[var(--brand)] font-medium" : "hover:bg-surface-2"
            )}
          >
            <Building2 className={cn("w-3 h-3 flex-shrink-0", isActive ? "text-[var(--brand)]" : "text-muted-foreground")} aria-hidden="true" />
            <span className="flex-1 truncate min-w-0">{org.name}</span>
            {isActive && <Check className="w-3 h-3 text-[var(--brand)] flex-shrink-0" aria-hidden="true" />}
          </button>
        );
      })}
      {!isDemoMode && (
        <>
          <div className="border-t border-border" aria-hidden="true" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setIsOrgDropdownOpen(false);
              setIsCreateOrgModalOpen(true);
            }}
            className="w-full flex items-center gap-2 px-2 py-2 text-left text-sm transition-colors motion-reduce:transition-none hover:bg-surface-2 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
          >
            <Plus className="w-3 h-3 text-muted-foreground flex-shrink-0" aria-hidden="true" />
            <span className="text-muted-foreground">New Organization</span>
          </button>
        </>
      )}
    </div>
  );

  if (inline) {
    return (
      <button
        type="button"
        onClick={goToWorkspaces}
        disabled={isDemoMode}
        aria-label={`Workspace ${activeWorkspace.name} — go to workspaces page`}
        className={cn(
          "flex items-center gap-2 px-4 transition-opacity motion-reduce:transition-none disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0",
          !isDemoMode && "hover:opacity-75"
        )}
        style={{ height: "var(--header-height)" }}
      >
        <span
          className={cn(
            "font-medium text-foreground truncate",
            large ? "text-lg font-semibold max-w-[200px]" : "text-sm max-w-[160px]"
          )}
        >
          {activeWorkspace.name}
        </span>
      </button>
    );
  }

  if (compact) {
    return (
      <>
        <div
          className="w-full bg-surface-1 border border-border overflow-visible"
          ref={orgDropdownRef}
        >
          <div className="relative">
            <button
              id={orgToggleId}
              ref={orgTriggerRef}
              type="button"
              onClick={() => setIsOrgDropdownOpen((prev) => !prev)}
              disabled={isDemoMode}
              aria-haspopup="menu"
              aria-controls={orgListId}
              aria-expanded={!isDemoMode && isOrgDropdownOpen}
              aria-label={`Organization ${activeOrg.name}${isDemoMode ? " (read-only in demo)" : " — open menu to switch"}`}
              className={cn(
                "w-full flex items-center gap-1.5 px-2.5 pt-2 pb-1 text-left transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0",
                !isDemoMode && "hover:bg-surface-2 cursor-pointer",
                isDemoMode && "cursor-not-allowed"
              )}
            >
              <Building2 className="w-3 h-3 text-muted-foreground flex-shrink-0" aria-hidden="true" />
              <span className="text-[10px] text-muted-foreground truncate flex-1 min-w-0">
                {activeOrg.name}
              </span>
              {!isDemoMode && (
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    "w-3 h-3 text-muted-foreground transition-transform motion-reduce:transition-none flex-shrink-0",
                    isOrgDropdownOpen && "rotate-180"
                  )}
                />
              )}
            </button>

            {isOrgDropdownOpen && (
              <div className="absolute left-0 right-0 top-full z-50">
                {orgDropdown("100%")}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={goToWorkspaces}
            disabled={isDemoMode}
            aria-label={`Workspace ${activeWorkspace.name} — go to workspaces page`}
            className={cn(
              "w-full flex items-center gap-1.5 px-2.5 pb-2 pt-1 text-left transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0 disabled:cursor-not-allowed",
              !isDemoMode && "hover:bg-surface-2"
            )}
          >
            <Briefcase className="w-3 h-3 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">
              {activeWorkspace.name}
            </span>
          </button>
        </div>

        <CreateOrgModal
          isOpen={isCreateOrgModalOpen}
          onClose={() => setIsCreateOrgModalOpen(false)}
        />
      </>
    );
  }

  return null;
}
